import type { ReactNode } from "react";
import { styled } from "../../../../styled-system/jsx";
import { flex } from "../../../../styled-system/patterns";
import { cx } from "../../../../styled-system/css";
import { ToggleButton, ToggleButtonGroup, type Key } from "react-aria-components";

export interface ButtonDefinition<T extends string> {
  label: ReactNode;
  value: T;
  counter?: number;
}

interface Props<T extends string> {
  buttons: ButtonDefinition<T>[];
  value: T;
  onSelect: (value: T) => void;
  compact?: boolean;
  variant?: "inline";
  colorPalette?: string;
  className?: string;
}

const StyledItem = styled(ToggleButton, {
  base: {
    colorPalette: "buttonGroup.mauve",
    display: "flex",
    gap: "small",
    justifyContent: "center",
    alignItems: "center",
    border: "buttonGroup",
    borderRight: "none",
    paddingY: "small",
    paddingX: "medium",
    color: "colorPalette.neutral.text",
    fontSize: "small",
    backgroundColor: "colorPalette.neutral.backgroundColor",
    transition: "all 60ms ease-in",
    _first: {
      borderTopLeftRadius: "button",
      borderBottomLeftRadius: "button",
    },
    _last: {
      borderRight: "buttonGroup",
      borderTopRightRadius: "button",
      borderBottomRightRadius: "button",
    },
    _focus: {
      color: "colorPalette.neutral.text.highlight",
      backgroundColor: "colorPalette.neutral.backgroundColor.highlight",
    },
    _hover: {
      color: "colorPalette.neutral.text.highlight",
      backgroundColor: "colorPalette.neutral.backgroundColor.highlight",
    },
    "&[data-selected], &[aria-pressed='true']": {
      color: "colorPalette.selected.text",
      backgroundColor: "colorPalette.selected.backgroundColor",
      _focus: {
        color: "colorPalette.selected.text.highlight",
        backgroundColor: "colorPalette.selected.backgroundColor.highlight",
      },
      _hover: {
        color: "colorPalette.selected.text.highlight",
        backgroundColor: "colorPalette.selected.backgroundColor.highlight",
      },
    },
  },
  variants: {
    compact: {
      true: {
        paddingY: "xsmall",
        paddingX: "small",
      },
    },
    variant: {
      inline: {
        border: "none",
        paddingY: "xsmall",
        paddingX: "xsmall",
        color: "grey",
        fontSize: "large",
        backgroundColor: "transparent",
        _first: {
          border: "none",
        },
        _last: {
          border: "none",
          borderRight: "none",
        },
        "&[aria-pressed='true']": {
          color: "colorPalette.selected.backgroundColor",
          backgroundColor: "transparent",
          _focus: {
            color: "colorPalette.selected.backgroundColor.highlight",
            backgroundColor: "colorPalette.neutral.backgroundColor.highlight",
          },
          _hover: {
            color: "colorPalette.selected.backgroundColor.highlight",
            backgroundColor: "colorPalette.neutral.backgroundColor.highlight",
          },
        },
      },
    },
  },
});

export const Badge = styled("span", {
  base: {
    borderRadius: "pill",
    padding: "badgePadding",
    color: "white",
    fontSize: "xsmall",
    fontWeight: "bold",
    lineHeight: "1",
    backgroundColor: "trellisPalette.3",
    "[data-selected] & , &[aria-pressed='true'] &": {
      color: "darkgrey",
      backgroundColor: "lightgrey",
    },
  },
});

export function ButtonGroup<T extends string>({
  buttons,
  value,
  onSelect,
  compact,
  colorPalette,
  className,
  variant,
}: Props<T>) {
  return (
    <ToggleButtonGroup
      selectionMode="single"
      selectedKeys={[value]}
      onSelectionChange={(keys) => {
        const selected = [...(keys as Iterable<Key>)][0] as T | undefined;
        if (selected !== undefined) onSelect(selected);
      }}
      className={cx(flex(), className)}
    >
      {buttons.map((button) => (
        <StyledItem
          id={button.value}
          key={button.value}
          compact={compact}
          variant={variant}
          colorPalette={colorPalette}
        >
          {button.label}
          {button.counter !== undefined && <Badge>{button.counter}</Badge>}
        </StyledItem>
      ))}
    </ToggleButtonGroup>
  );
}
